import { useEffect, useState } from "react";
import { Phone, Mail, CalendarCheck, CheckCircle2, XCircle } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { adminApi, STATUS, CHECKIN, EMAIL_KINDS, Badge, fmtDate, fmtDateTime } from "./adminApi";

const Info = ({ label, children }) => (
  <div><dt className="text-xs text-[#526356]">{label}</dt><dd className="text-sm text-[#1B3B28] mt-0.5">{children}</dd></div>
);

const EmailTimeline = ({ logs }) => (
  <ol className="relative border-l border-[#1B3B28]/15 ml-2 mt-4 space-y-5" data-testid="booking-email-history">
    {logs.map((l, i) => (
      <li key={i} className="pl-5 relative" data-testid={`email-log-${i}`}>
        <span className={`absolute -left-[9px] top-0.5 bg-[#F4F3EC] ${l.status === "inviata" ? "text-[#3E6B48]" : "text-[#9A4B3C]"}`}>
          {l.status === "inviata" ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
        </span>
        <p className="text-sm text-[#1B3B28]">{EMAIL_KINDS[l.kind] || l.kind}</p>
        <p className="text-xs text-[#526356] mt-0.5">{fmtDateTime(l.sent_at)} · {l.to} · {l.status}</p>
        <p className="text-xs text-[#526356]/80 mt-0.5 italic">{l.subject}</p>
      </li>
    ))}
    {logs.length === 0 && <li className="pl-5 text-sm text-[#526356]">Nessuna email registrata (lo storico parte da oggi).</li>}
  </ol>
);

export const BookingDrawer = ({ booking: b, onClose }) => {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    if (!b) return;
    setLogs([]);
    adminApi.get(`/admin/bookings/${b.id}/emails`).then(({ data }) => setLogs(data));
  }, [b]);

  return (
    <Sheet open={!!b} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-lenis-prevent className="w-full sm:max-w-lg overflow-y-auto bg-[#F4F3EC] border-[#1B3B28]/10" data-testid="booking-drawer">
        {b && (
          <>
            <Badge cls={STATUS[b.status].cls}>{STATUS[b.status].label}</Badge>
            <SheetTitle className="f-serif text-4xl font-normal text-[#1B3B28] mt-3">{b.nome} {b.cognome}</SheetTitle>
            <SheetDescription className="text-[#526356]">Richiesta del {fmtDate(b.created_at, { day: "numeric", month: "long", year: "numeric" })}</SheetDescription>
            <div className="mt-5 flex flex-wrap gap-2">
              <a href={`tel:${b.telefono}`} className="flex items-center gap-2 rounded-full bg-[#FCFBF7] border border-[#1B3B28]/10 px-4 py-2 text-sm text-[#1B3B28]" data-testid="drawer-call-btn"><Phone size={14} /> {b.telefono}</a>
              <a href={`mailto:${b.email}`} className="flex items-center gap-2 rounded-full bg-[#FCFBF7] border border-[#1B3B28]/10 px-4 py-2 text-sm text-[#1B3B28] break-all" data-testid="drawer-email-btn"><Mail size={14} /> {b.email}</a>
            </div>
            <dl className="mt-6 grid grid-cols-2 gap-4 rounded-2xl bg-[#FCFBF7] p-5">
              <Info label="Visita">{b.appointment_date ? `${fmtDate(b.appointment_date, { weekday: "long", day: "numeric", month: "long" })} · ${b.appointment_time}` : "Da fissare"}</Info>
              <Info label="Trattamento"><span data-testid="drawer-treatment">{b.trattamento === "igiene_gbt" ? "Seduta di igiene GBT" : "Visita parodontale"}</span></Info>
              <Info label="Seduta laser">{b.laser_session ? "Sì" : "No"}</Info>
              <Info label="Preferenza">{b.preferenza}</Info>
              <Info label="Ansia">{b.ansia}/10</Info>
              <div className="col-span-2"><Info label="Sintomi">{b.sintomi.join(", ") || "Nessuno indicato"}</Info></div>
              {b.note && <div className="col-span-2"><Info label="Note">{b.note}</Info></div>}
              {b.checkin_response && (
                <div className="col-span-2"><Info label="Come ti senti?"><Badge cls={CHECKIN[b.checkin_response].cls}>{CHECKIN[b.checkin_response].label}</Badge>{b.checkin_note && <span className="block mt-1 italic">“{b.checkin_note}”</span>}</Info></div>
              )}
            </dl>
            {b.manage_token && (
              <a href={`/visita/${b.manage_token}`} target="_blank" rel="noopener noreferrer" data-testid="drawer-manage-link"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] py-3.5 text-sm hover:bg-[#3E6B48] transition-colors">
                <CalendarCheck size={16} /> {b.appointment_date ? "Modifica data della visita" : "Fissa la data della visita"}
              </a>
            )}
            <p className="eyebrow text-[#3E6B48] mt-8">Email inviate</p>
            <EmailTimeline logs={logs} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
